import { Link } from "react-router-dom";
import { ArrowLeft, ArrowsHorizontal } from "@phosphor-icons/react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from "recharts";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { fmtCurrency } from "@/lib/format";
import { useCompareData } from "@/hooks/useCompareData";

const CHART_MARGIN = { top: 10, right: 20, left: 0, bottom: 10 };
const AXIS_TICK_X = { fill: "#6B7264", fontSize: 12 };
const AXIS_TICK_Y = { fill: "#6B7264", fontSize: 11 };
const AXIS_LINE = { stroke: "#E5E2D9" };
const TOOLTIP_STYLE = { background: "white", border: "1px solid #E5E2D9", borderRadius: 6 };
const LEGEND_STYLE = { fontSize: 12 };
const yTickFormatter = (v) => fmtCurrency(v, { compact: true });

export default function Compare() {
  const compare = useCompareData();

  return (
    <main data-testid="compare-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-14">
      <div className="mb-10">
        <Link to="/scenarios" className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-forest transition-colors">
          <ArrowLeft size={14} weight="bold" /> All scenarios
        </Link>
        <span className="mt-3 block label-eyebrow">Side-by-side</span>
        <h1 className="mt-2 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
          Compare two scenarios
        </h1>
        <p className="mt-3 text-brand-ink/70 max-w-2xl">
          Helpful for showing a client "your current path vs. a tuned plan", or comparing two
          clients side-by-side.
        </p>
      </div>

      <CompareBody {...compare} />
    </main>
  );
}

function CompareBody({ loading, scenarios, a, b, aId, bId, setAId, setBId, opts, chartData }) {
  if (loading) {
    return <div className="bg-white border border-brand-line rounded-md p-8 h-40 animate-pulse" />;
  }
  if (scenarios.length < 2) {
    return <NeedTwoScenarios />;
  }
  const stillFetching = (aId && !a) || (bId && !b);
  const needsPicks = !aId || !bId;
  return (
    <>
      <div className="grid md:grid-cols-2 gap-5 mb-8">
        <Picker label="Scenario A" value={aId} onChange={setAId} opts={opts} testId="compare-select-a" />
        <Picker label="Scenario B" value={bId} onChange={setBId} opts={opts} testId="compare-select-b" />
      </div>

      {a && b && (
        <>
          <DiffHeadline a={a} b={b} />
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <BreakdownPanel scenario={a} />
            <BreakdownPanel scenario={b} />
          </div>
          <TrajectoryChart a={a} b={b} chartData={chartData} />
        </>
      )}

      {stillFetching && <p className="text-sm text-brand-muted">Loading scenario…</p>}
      {needsPicks && (
        <div className="bg-white border border-brand-line border-dashed rounded-md p-10 text-center text-brand-muted text-sm">
          Pick a scenario for both A and B to see the comparison.
        </div>
      )}
    </>
  );
}

function NeedTwoScenarios() {
  return (
    <div className="bg-white border border-brand-line border-dashed rounded-md p-14 text-center">
      <h3 className="font-serif text-2xl text-brand-forest">Save at least two scenarios first</h3>
      <p className="mt-3 text-sm text-brand-ink/60 max-w-md mx-auto">
        Run a breakdown twice — once with the current plan, once with the tuned plan — and you
        can compare them here.
      </p>
      <Link to="/calculator" className="mt-6 inline-flex items-center gap-2 rounded-md bg-brand-forest hover:bg-brand-forestHover text-brand-bone px-5 py-3 text-sm font-semibold">
        Open calculator
      </Link>
    </div>
  );
}

function DiffHeadline({ a, b }) {
  return (
    <div className="bg-brand-forest text-brand-bone rounded-md p-8 md:p-10 mb-8 grain relative overflow-hidden" data-testid="compare-diff">
      <div className="relative grid md:grid-cols-3 gap-6 items-center">
        <Headline label="Scenario A" client={a.client_name} value={a.result.headline_wealth_gain} />
        <div className="text-center">
          <ArrowsHorizontal size={28} weight="duotone" className="text-brand-copper mx-auto" />
          <div className="mt-2 text-[10px] uppercase tracking-[0.2em] text-brand-bone/60">Difference</div>
          <div className="mt-2 font-serif text-3xl md:text-4xl text-brand-copper">
            {fmtCurrency(Math.abs(a.result.headline_wealth_gain - b.result.headline_wealth_gain))}
          </div>
        </div>
        <Headline label="Scenario B" client={b.client_name} value={b.result.headline_wealth_gain} right />
      </div>
    </div>
  );
}

function TrajectoryChart({ a, b, chartData }) {
  return (
    <div className="bg-white border border-brand-line rounded-md p-6 md:p-8">
      <span className="label-eyebrow">Year-by-year wealth gain</span>
      <h3 className="mt-2 font-serif text-3xl text-brand-forest mb-6">Trajectories</h3>
      <div className="h-[380px]">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={chartData} margin={CHART_MARGIN}>
            <CartesianGrid stroke="#E5E2D9" vertical={false} />
            <XAxis dataKey="year" tick={AXIS_TICK_X} tickLine={false} axisLine={AXIS_LINE} />
            <YAxis tick={AXIS_TICK_Y} tickLine={false} axisLine={AXIS_LINE} tickFormatter={yTickFormatter} width={85} />
            <Tooltip
              formatter={(v) => fmtCurrency(v)}
              contentStyle={TOOLTIP_STYLE}
            />
            <Legend wrapperStyle={LEGEND_STYLE} />
            <Line type="monotone" dataKey={`A · ${a.client_name}`} stroke="#113C2B" strokeWidth={2.5} dot={false} />
            <Line type="monotone" dataKey={`B · ${b.client_name}`} stroke="#B8860B" strokeWidth={2.5} dot={false} strokeDasharray="6 4" />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function Picker({ label, value, onChange, opts, testId }) {
  return (
    <div>
      <div className="label-eyebrow mb-2">{label}</div>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="bg-white border-brand-line h-12 rounded-md">
          <SelectValue placeholder="Select a saved scenario…" />
        </SelectTrigger>
        <SelectContent className="bg-white border-brand-line">
          {opts.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function Headline({ label, client, value, right }) {
  return (
    <div className={right ? "text-right" : ""}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-brand-bone/60">{label}</div>
      <div className="mt-2 font-serif text-2xl md:text-3xl text-brand-bone leading-tight">
        {client}
      </div>
      <div className="mt-2 font-serif text-3xl md:text-4xl text-brand-bone">
        {fmtCurrency(value)}
      </div>
    </div>
  );
}

function BreakdownPanel({ scenario }) {
  const r = scenario.result;
  const i = scenario.inputs;
  const rows = [
    ["Tax drag saved", r.headline_tax_drag_saved],
    ["Interest recaptured", r.headline_interest_recaptured],
    ["Retention value", r.headline_retention_value],
    ["IBC cash value", r.headline_ibc_cash_value],
    ["Bank-path value", r.headline_bank_value],
  ];
  return (
    <div className="bg-white border border-brand-line rounded-md p-6">
      <div className="label-eyebrow">{scenario.business_name || "Client"}</div>
      <h3 className="mt-2 font-serif text-2xl text-brand-forest leading-tight">{scenario.client_name}</h3>
      <div className="mt-4 text-xs text-brand-muted">
        {i.horizon_years}-yr · {i.num_vehicles} vehicles · {fmtCurrency(i.annual_contribution, { compact: true })}/yr · {i.num_key_employees} key staff
      </div>
      <div className="mt-5 pt-4 border-t border-brand-line space-y-3">
        {rows.map(([k, v]) => (
          <div key={k} className="flex justify-between items-baseline text-sm">
            <span className="text-brand-ink/70">{k}</span>
            <span className="font-semibold tabular-nums text-brand-forest">{fmtCurrency(v)}</span>
          </div>
        ))}
        <div className="flex justify-between items-baseline pt-3 border-t border-brand-line">
          <span className="text-xs uppercase tracking-[0.16em] text-brand-muted">{i.horizon_years}-yr total gain</span>
          <span className="font-serif text-2xl text-brand-copper">{fmtCurrency(r.headline_wealth_gain)}</span>
        </div>
      </div>
    </div>
  );
}
